import React, { useEffect, useRef } from 'react';

const MetaverseCanvas: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationId: number;
    let mouseX = 0;
    let mouseY = 0;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const handleMouseMove = (e: MouseEvent) => {
      mouseX = (e.clientX / window.innerWidth - 0.5) * 2;
      mouseY = (e.clientY / window.innerHeight - 0.5) * 2;
    };
    window.addEventListener('mousemove', handleMouseMove);

    // 3D粒子系统
    interface Particle3D {
      x: number;
      y: number;
      z: number;
      vx: number;
      vy: number;
      vz: number;
      color: string;
      size: number;
    }

    const particles: Particle3D[] = [];
    const particleCount = 300;
    const colors = ['#00e5ff', '#8b5cf6', '#f0c040', '#f472b6'];

    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: (Math.random() - 0.5) * 2000,
        y: (Math.random() - 0.5) * 2000,
        z: Math.random() * 2000,
        vx: (Math.random() - 0.5) * 2,
        vy: (Math.random() - 0.5) * 2,
        vz: -Math.random() * 3 - 1,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: Math.random() * 3 + 1
      });
    }

    // 3D几何体
    interface Geometry3D {
      type: 'cube' | 'sphere' | 'torus';
      x: number;
      y: number;
      z: number;
      rotationX: number;
      rotationY: number;
      rotationZ: number;
      rotationSpeedX: number;
      rotationSpeedY: number;
      rotationSpeedZ: number;
      size: number;
      color: string;
    }

    const geometries: Geometry3D[] = [
      {
        type: 'cube',
        x: -400,
        y: -200,
        z: 800,
        rotationX: 0,
        rotationY: 0,
        rotationZ: 0,
        rotationSpeedX: 0.01,
        rotationSpeedY: 0.015,
        rotationSpeedZ: 0.005,
        size: 100,
        color: '#00e5ff'
      },
      {
        type: 'sphere',
        x: 400,
        y: 200,
        z: 1000,
        rotationX: 0,
        rotationY: 0,
        rotationZ: 0,
        rotationSpeedX: 0.005,
        rotationSpeedY: 0.01,
        rotationSpeedZ: 0.008,
        size: 120,
        color: '#8b5cf6'
      },
      {
        type: 'torus',
        x: 0,
        y: 0,
        z: 600,
        rotationX: 0,
        rotationY: 0,
        rotationZ: 0,
        rotationSpeedX: 0.008,
        rotationSpeedY: 0.012,
        rotationSpeedZ: 0.006,
        size: 150,
        color: '#f0c040'
      }
    ];

    // 3D投影
    const project = (x: number, y: number, z: number) => {
      const fov = 500;
      const scale = fov / (fov + z);
      return {
        x: x * scale + canvas.width / 2,
        y: y * scale + canvas.height / 2,
        scale: scale
      };
    };

    // 绘制3D网格地面
    const drawGrid = () => {
      const gridSize = 100;
      const gridCount = 20;
      
      ctx.strokeStyle = 'rgba(0, 229, 255, 0.15)';
      ctx.lineWidth = 1;

      // 水平线
      for (let i = -gridCount; i <= gridCount; i++) {
        const y = 300;
        const z1 = i * gridSize;
        const z2 = i * gridSize;
        
        const p1 = project(-1000 + mouseX * 50, y, z1);
        const p2 = project(1000 + mouseX * 50, y, z2);
        
        ctx.beginPath();
        ctx.moveTo(p1.x, p1.y);
        ctx.lineTo(p2.x, p2.y);
        ctx.stroke();
      }

      // 垂直线
      for (let i = -gridCount; i <= gridCount; i++) {
        const x = i * gridSize;
        
        const p1 = project(x + mouseX * 50, 300, -1000);
        const p2 = project(x + mouseX * 50, 300, 1000);
        
        ctx.beginPath();
        ctx.moveTo(p1.x, p1.y);
        ctx.lineTo(p2.x, p2.y);
        ctx.stroke();
      }
    };

    // 绘制3D立方体
    const drawCube = (geo: Geometry3D) => {
      const size = geo.size;
      const vertices = [
        [-size, -size, -size], [size, -size, -size],
        [size, size, -size], [-size, size, -size],
        [-size, -size, size], [size, -size, size],
        [size, size, size], [-size, size, size]
      ];

      const edges = [
        [0, 1], [1, 2], [2, 3], [3, 0],
        [4, 5], [5, 6], [6, 7], [7, 4],
        [0, 4], [1, 5], [2, 6], [3, 7]
      ];

      ctx.strokeStyle = geo.color;
      ctx.lineWidth = 2;
      ctx.globalAlpha = 0.8;

      edges.forEach(([i, j]) => {
        let [x1, y1, z1] = vertices[i];
        let [x2, y2, z2] = vertices[j];

        // 旋转
        const rotate = (x: number, y: number, z: number) => {
          // X轴旋转
          let cosX = Math.cos(geo.rotationX);
          let sinX = Math.sin(geo.rotationX);
          let y1 = y * cosX - z * sinX;
          let z1 = y * sinX + z * cosX;
          y = y1; z = z1;

          // Y轴旋转
          let cosY = Math.cos(geo.rotationY);
          let sinY = Math.sin(geo.rotationY);
          let x1 = x * cosY + z * sinY;
          let z2 = -x * sinY + z * cosY;
          x = x1; z = z2;

          // Z轴旋转
          let cosZ = Math.cos(geo.rotationZ);
          let sinZ = Math.sin(geo.rotationZ);
          let x2 = x * cosZ - y * sinZ;
          let y2 = x * sinZ + y * cosZ;
          x = x2; y = y2;

          return [x, y, z];
        };

        [x1, y1, z1] = rotate(x1, y1, z1);
        [x2, y2, z2] = rotate(x2, y2, z2);

        const p1 = project(x1 + geo.x + mouseX * 30, y1 + geo.y + mouseY * 30, z1 + geo.z);
        const p2 = project(x2 + geo.x + mouseX * 30, y2 + geo.y + mouseY * 30, z2 + geo.z);

        ctx.beginPath();
        ctx.moveTo(p1.x, p1.y);
        ctx.lineTo(p2.x, p2.y);
        ctx.stroke();
      });

      ctx.globalAlpha = 1;
    };

    // 绘制3D球体
    const drawSphere = (geo: Geometry3D) => {
      const segments = 20;
      const radius = geo.size;

      ctx.strokeStyle = geo.color;
      ctx.lineWidth = 1.5;
      ctx.globalAlpha = 0.6;

      // 绘制经线
      for (let i = 0; i < segments; i++) {
        const theta = (i / segments) * Math.PI * 2;
        
        ctx.beginPath();
        for (let j = 0; j <= segments; j++) {
          const phi = (j / segments) * Math.PI;
          
          let x = radius * Math.sin(phi) * Math.cos(theta);
          let y = radius * Math.cos(phi);
          let z = radius * Math.sin(phi) * Math.sin(theta);

          // 旋转
          const cosY = Math.cos(geo.rotationY);
          const sinY = Math.sin(geo.rotationY);
          const x1 = x * cosY + z * sinY;
          const z1 = -x * sinY + z * cosY;
          x = x1; z = z1;

          const cosX = Math.cos(geo.rotationX);
          const sinX = Math.sin(geo.rotationX);
          const y1 = y * cosX - z * sinX;
          const z2 = y * sinX + z * cosX;
          y = y1; z = z2;

          const p = project(x + geo.x + mouseX * 30, y + geo.y + mouseY * 30, z + geo.z);
          
          if (j === 0) {
            ctx.moveTo(p.x, p.y);
          } else {
            ctx.lineTo(p.x, p.y);
          }
        }
        ctx.stroke();
      }

      // 绘制纬线
      for (let i = 1; i < segments; i++) {
        const phi = (i / segments) * Math.PI;
        
        ctx.beginPath();
        for (let j = 0; j <= segments; j++) {
          const theta = (j / segments) * Math.PI * 2;
          
          let x = radius * Math.sin(phi) * Math.cos(theta);
          let y = radius * Math.cos(phi);
          let z = radius * Math.sin(phi) * Math.sin(theta);

          const cosY = Math.cos(geo.rotationY);
          const sinY = Math.sin(geo.rotationY);
          const x1 = x * cosY + z * sinY;
          const z1 = -x * sinY + z * cosY;
          x = x1; z = z1;

          const cosX = Math.cos(geo.rotationX);
          const sinX = Math.sin(geo.rotationX);
          const y1 = y * cosX - z * sinX;
          const z2 = y * sinX + z * cosX;
          y = y1; z = z2;

          const p = project(x + geo.x + mouseX * 30, y + geo.y + mouseY * 30, z + geo.z);
          
          if (j === 0) {
            ctx.moveTo(p.x, p.y);
          } else {
            ctx.lineTo(p.x, p.y);
          }
        }
        ctx.stroke();
      }

      ctx.globalAlpha = 1;
    };

    // 绘制3D环面
    const drawTorus = (geo: Geometry3D) => {
      const segments = 30;
      const tubeSegments = 20;
      const radius = geo.size;
      const tubeRadius = geo.size * 0.3;

      ctx.strokeStyle = geo.color;
      ctx.lineWidth = 1.5;
      ctx.globalAlpha = 0.7;

      for (let i = 0; i < segments; i++) {
        const theta = (i / segments) * Math.PI * 2;
        
        ctx.beginPath();
        for (let j = 0; j <= tubeSegments; j++) {
          const phi = (j / tubeSegments) * Math.PI * 2;
          
          let x = (radius + tubeRadius * Math.cos(phi)) * Math.cos(theta);
          let y = (radius + tubeRadius * Math.cos(phi)) * Math.sin(theta);
          let z = tubeRadius * Math.sin(phi);

          // 旋转
          const cosY = Math.cos(geo.rotationY);
          const sinY = Math.sin(geo.rotationY);
          const x1 = x * cosY + z * sinY;
          const z1 = -x * sinY + z * cosY;
          x = x1; z = z1;

          const cosX = Math.cos(geo.rotationX);
          const sinX = Math.sin(geo.rotationX);
          const y1 = y * cosX - z * sinX;
          const z2 = y * sinX + z * cosX;
          y = y1; z = z2;

          const p = project(x + geo.x + mouseX * 30, y + geo.y + mouseY * 30, z + geo.z);
          
          if (j === 0) {
            ctx.moveTo(p.x, p.y);
          } else {
            ctx.lineTo(p.x, p.y);
          }
        }
        ctx.stroke();
      }

      ctx.globalAlpha = 1;
    };

    // 动画循环
    const animate = () => {
      // 清除画布（带拖尾效果）
      ctx.fillStyle = 'rgba(5, 6, 15, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // 绘制网格
      drawGrid();

      // 绘制几何体
      geometries.forEach(geo => {
        geo.rotationX += geo.rotationSpeedX;
        geo.rotationY += geo.rotationSpeedY;
        geo.rotationZ += geo.rotationSpeedZ;

        if (geo.type === 'cube') {
          drawCube(geo);
        } else if (geo.type === 'sphere') {
          drawSphere(geo);
        } else if (geo.type === 'torus') {
          drawTorus(geo);
        }
      });

      // 更新和绘制粒子
      particles.forEach(p => {
        p.x += p.vx;
        p.y += p.vy;
        p.z += p.vz;

        // 重置粒子
        if (p.z < 0) {
          p.z = 2000;
          p.x = (Math.random() - 0.5) * 2000;
          p.y = (Math.random() - 0.5) * 2000;
        }

        // 投影
        const projected = project(p.x + mouseX * 50, p.y + mouseY * 50, p.z);
        const size = p.size * projected.scale;
        const alpha = (1 - p.z / 2000) * 0.8;

        ctx.fillStyle = p.color;
        ctx.globalAlpha = alpha;
        ctx.beginPath();
        ctx.arc(projected.x, projected.y, size, 0, Math.PI * 2);
        ctx.fill();
      });

      ctx.globalAlpha = 1;

      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  return <canvas ref={canvasRef} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1 }} />;
};

export default MetaverseCanvas;
